import { FC, useEffect, useState } from "react";
import { CarDataType } from "data/types";
import { useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import { CarListingModal } from "models/vehicleModal";
import FleetCard from "designs/DesignA/components/FleetCard/FleetCard";
import TabFilters from "designs/Common/TabFilters";

export interface SectionGridFilterCardProps {
  className?: string;
  data?: CarDataType[];
}

const SectionGridFilterCard: FC<SectionGridFilterCardProps> = ({
  className = "",
  data,
}) => {
  const { setSelectedCar, availableFleet } = useData();
  const [visibleCarsCount, setVisibleCarsCount] = useState<number>(8);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedMakes, setSelectedMakes] = useState<string[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<string>("default");
  const [maxPrice, setMaxPrice] = useState(0);
  const [filteredFleet, setFilteredFleet] = useState<CarListingModal[]>(
    availableFleet || []
  );
  const [rangePrices, setRangePrices] = useState([0, maxPrice]);
  const navigate = useNavigate();

  useEffect(() => {
    if (availableFleet && availableFleet.length > 0) {
      const calculatedMaxPrice = availableFleet.reduce(
        (acc, current) => Math.max(acc, current.avg_car_price_per_day ?? 0),
        0
      );
      setMaxPrice(calculatedMaxPrice);
    }
  }, [availableFleet]);

  useEffect(() => {
    if (maxPrice > 0) {
      setRangePrices([0, maxPrice]);
    }
  }, [maxPrice]);

  const getUniqueFleetClasses = () => {
    return Array.from(
      new Set(availableFleet?.map((car) => car?.fleet_class?.name ?? "") || [])
    ).filter((item): item is string => item !== undefined && item !== "");
  };

  const getUniqueMakes = () => {
    return Array.from(
      new Set(availableFleet?.map((car) => car?.make ?? "") || [])
    ).filter((item): item is string => item !== undefined && item !== "");
  };

  const getUniqueModels = () => {
    return Array.from(
      new Set(availableFleet?.map((car) => car?.model ?? "") || [])
    ).filter((item): item is string => item !== undefined && item !== "");
  };

  // Check if any vehicles have make data
  const hasMakeData = () => {
    return availableFleet?.some((car) => car?.make && car.make !== "") || false;
  };

  // Check if any vehicles have model data
  const hasModelData = () => {
    return availableFleet?.some((car) => car?.model && car.model !== "") || false;
  };

  useEffect(() => {
    if (availableFleet) {
      let filtered = [...availableFleet];
      filtered = filtered.filter(
        (item) =>
          (item.avg_car_price_per_day ?? 0) >= rangePrices[0] &&
          (item.avg_car_price_per_day ?? 0) <= rangePrices[1]
      );

      if (
        selectedTypes.length > 0 &&
        selectedTypes.length < getUniqueFleetClasses().length
      ) {
        filtered = filtered.filter((item: CarListingModal) =>
          selectedTypes.includes(item?.fleet_class?.name ?? "")
        );
      }
      if (
        selectedMakes.length > 0 &&
        selectedMakes.length < getUniqueMakes().length
      ) {
        filtered = filtered.filter((item: CarListingModal) =>
          selectedMakes.includes(item?.make ?? "")
        );
      }

      if (
        selectedModels.length > 0 &&
        selectedModels.length < getUniqueModels().length
      ) {
        filtered = filtered.filter((item: CarListingModal) =>
          selectedModels.includes(item?.model ?? "")
        );
      }

      if (sortOrder === "aToZ") {
        filtered.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
      } else if (sortOrder === "zToA") {
        filtered.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameB.localeCompare(nameA);
        });
      } else if (sortOrder === "priceLowToHigh") {
        filtered.sort(
          (a, b) =>
            (a.avg_car_price_per_day ?? 0) - (b.avg_car_price_per_day ?? 0)
        );
      } else if (sortOrder === "priceHighToLow") {
        filtered.sort(
          (a, b) =>
            (b.avg_car_price_per_day ?? 0) - (a.avg_car_price_per_day ?? 0)
        );
      }

      setFilteredFleet(filtered);

      if (filtered.length <= visibleCarsCount) {
        setExpanded(false);
      }
    }
  }, [
    availableFleet,
    selectedTypes,
    selectedMakes,
    selectedModels,
    rangePrices,
    sortOrder,
  ]);

  const renderCard = (car: CarListingModal) => {

    return (
      <FleetCard
        key={car.id}
        data={car}
        onClick={() => {
          navigate("/listing-car-detail?selectedCarId=" + car.id);
          setSelectedCar(car);
        }}
      />
    );
  };

  return (
    <div
      className={`nc-SectionGridFilterCard ${className}`}
      style={{ minHeight: "80vh" }}
      data-nc-id="SectionGridFilterCard"
    >
      <div className="flex lg:flex-row flex-col justify-between">
        <div className={`mb-4 lg:mb-16`}>
          <h2 className="text-2xl md:text-4xl font-bold text-text-color">
            {"Available Fleet (" + filteredFleet?.length + ")"}
          </h2>
        </div>

        <div className="mb-12 lg:mb-12">
          <TabFilters
            types={[...getUniqueFleetClasses()]}
            makes={hasMakeData() ? [...getUniqueMakes()] : []}
            models={hasModelData() ? [...getUniqueModels()] : []}
            selectedTypes={selectedTypes}
            selectedMakes={selectedMakes}
            selectedModels={selectedModels}
            setSelectedTypes={setSelectedTypes}
            setSelectedMakes={setSelectedMakes}
            setSelectedModels={setSelectedModels}
            rangePrices={rangePrices}
            setRangePrices={setRangePrices}
            maxPrice={maxPrice}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            showMakeFilter={hasMakeData()}
            showModelFilter={hasModelData()}
          />
        </div>
      </div>
      <div className="nc-SectionGridFeaturePlaces relative">
        <div
          className={`grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4`}
        >
          {filteredFleet?.map(
            (car: any) => car?.active === true && renderCard(car)
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionGridFilterCard;
